import { Button, Dialog, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { WalletBalance } from "@/pages/CustomerDashboard/PointsExpirationBanner";
import { Color } from "@/themes/MUITheme/palette";
import { Divider, Stack, useTheme } from '@mui/material';
import { aggregateBalances, groupByDaysUntilExpiration } from "@shared/utilities";
import React from "react";
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";

interface PointsExpirationViewAllDialogProps {
  onNavigateToWallet?: () => void,
  onClose: () => void,
  walletBalances: WalletBalance[],
}

export type ExpiringWalletBalance = WalletBalance & { expirationDate: string }

const ExpiringWalletGroup = ({ expiringInDays, expiringWalletBalances, statusColor }: {
  expiringInDays: number,
  expiringWalletBalances?: ExpiringWalletBalance[],
  statusColor: Color,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!expiringWalletBalances) {
    return null;
  }

  const groupedWalletBalances: ExpiringWalletBalance[] = aggregateBalances(
    expiringWalletBalances,
    balance => `${balance.subwalletId}::${balance.expirationDate}`,
  );

  return (
    <Stack>
      <Text category="overline" color={statusColor}>
        {t(
          $ => $.customerDashboard.pointsExpiration.dialog.expiringInDays,
          { count: expiringInDays },
        )}
      </Text>
      <VSpacer size="3"/>
      <Divider/>
      {groupedWalletBalances.sort((a, b) =>
        a.expirationDate.localeCompare(b.expirationDate),
      ).map((expiringWalletBalance) => {
        return (
          <Stack key={`${expiringWalletBalance.subwalletId}::${expiringWalletBalance.expirationDate}`}>
            <VSpacer size="3"/>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row">
                {statusColor === 'error' && (
                  <>
                    <Stack>
                      <VSpacer size="1"/>
                      <MaterialSymbol
                        color={theme.palette['error'].main}
                        fill
                        icon="alarm"
                      />
                    </Stack>
                    <HSpacer size="1"/>
                    <HSpacer size="2"/>
                  </>
                )}
                <Stack>
                  <Text category="body-medium">
                    {t($ => $.wallet.expiringDate, { date: expiringWalletBalance.expirationDate })}
                  </Text>
                  <Text breakWord category="body-small">
                    {expiringWalletBalance.name}
                  </Text>
                </Stack>
              </Stack>
              <HSpacer size="8"/>
              <Stack>
                <Text category="body-medium" noWrap>
                  {t(
                    $ => $.common.units.pointsCount,
                    { count: expiringWalletBalance.balance },
                  )}
                </Text>
              </Stack>
            </Stack>
            <VSpacer size="1"/>
            <VSpacer size="3"/>
            <Divider/>
          </Stack>
        );
      })}
      <VSpacer size="7"/>
    </Stack>
  );
};

export const PointsExpirationViewAllDialog = ({
  onNavigateToWallet,
  onClose,
  walletBalances,
}: PointsExpirationViewAllDialogProps) => {
  const { t } = useTranslation();

  const expiringWalletGroups = groupByDaysUntilExpiration<WalletBalance>(walletBalances);

  return (
    <Dialog
      acceptButton={(props) => (
        <Stack direction="row">
          {!!onNavigateToWallet && (
            <>
              <Button
                {...props}
                onClick={onNavigateToWallet}
                variant="text"
              >
                {t($ => $.customerDashboard.pointsExpiration.dialog.goToWallet)}
              </Button>
              <HSpacer size="4"/>
            </>
          )}
          <Button
            {...props}
            onClick={onClose}
            variant="contained"
          >
            {t($ => $.common.actions.close)}
          </Button>
        </Stack>
      )}
      onClose={onClose}
      open
      showCloseButton={false}
      testID="points-expiring-soon-dialog"
      title={t($ => $.customerDashboard.pointsExpiration.dialog.title)}
    >
      <Stack>
        <ExpiringWalletGroup
          expiringInDays={7}
          expiringWalletBalances={expiringWalletGroups[7]}
          statusColor={'error'}
        />
        <ExpiringWalletGroup
          expiringInDays={30}
          expiringWalletBalances={expiringWalletGroups[30]}
          statusColor={'warning'}
        />
        <ExpiringWalletGroup
          expiringInDays={60}
          expiringWalletBalances={expiringWalletGroups[60]}
          statusColor={'info'}
        />
        <ExpiringWalletGroup
          expiringInDays={90}
          expiringWalletBalances={expiringWalletGroups[90]}
          statusColor={'info'}
        />
      </Stack>
    </Dialog>
  );
};
